import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'types/queryKeys';
import { SurveyResponse } from 'types/surveyResponse';
import { Product } from 'types/product';
import {
  upsertSurveyResponses,
  upsertProducts,
  deleteSurveyResponse,
  upsertModifiedDates,
  upsertBlob,
  deletePendingBlob
} from 'services/RxDBService';
import { ModifiedDate } from 'types/date';
import { blobToBase64 } from 'utils/blob';

export const useCreateSurveyResponse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newSurvey: SurveyResponse) => {
      queryClient.setQueryData([QueryKeys.SURVEY_RESPONSES, newSurvey.responseId], newSurvey);
      await upsertSurveyResponses([newSurvey]);
      return newSurvey;
    }
  });
};

export const useUpdateSurveyResponse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (surveyResponse: SurveyResponse) => {
      queryClient.setQueryData([QueryKeys.SURVEY_RESPONSES, surveyResponse.responseId], surveyResponse);
      const success = await upsertSurveyResponses([surveyResponse]);
      if (!success) {
        throw new Error('Failed to update survey response!');
      }
    },
    onError: async (error) => {
      throw new Error('Failed to update survey response!', error);
    }
  });
};

export const useDeleteSurveyResponse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (surveyResponse: SurveyResponse) => {
      queryClient.removeQueries({ queryKey: [QueryKeys.SURVEY_RESPONSES, surveyResponse.responseId] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.SURVEY_RESPONSES], exact: true });
      const success = await deleteSurveyResponse(surveyResponse.responseId);
      if (!success) {
        throw new Error('Failed to delete survey response!');
      }
    }
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (product: Product) => {
      queryClient.setQueryData([QueryKeys.PRODUCTS], product);
      await upsertProducts([product]);
    },
    onError: async (error) => {
      throw new Error('Failed to put product!', error);
    }
  });
};

export const useUpdateModifiedDate = () => {
  return useMutation({
    mutationFn: async (data: ModifiedDate) => {
      await upsertModifiedDates([data]);
    },
    onError: async (error) => {
      throw new Error('Failed to put modified date!', error);
    }
  });
};

export const useBlobMutation = () => {
  return useMutation({
    mutationFn: async ({ category, blobId, blob }: { category: string; blobId: string; blob: Blob }) => {
      await upsertBlob({ name: blobId, category, mimeType: blob.type, content: await blobToBase64(blob) });
    }
  });
};

export const useDeleteBlobMutation = () => {
  return useMutation({
    mutationFn: async (blobId: string) => {
      await deletePendingBlob(blobId);
    }
  });
};
